import React, { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { Galleria } from 'primereact/galleria';
import classNames from 'classnames';

import { FlexBox } from '@eltoro-ui/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagic,
  faCheck,
  faMinusCircle,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
} from '@fortawesome/pro-light-svg-icons';

import CanvasEditor from 'Components/CanvasEditor';

import CustomIcons from 'assets/icons';
import ToggleMenu from 'Pages/Orderlines/ToggleMenu';
import EditWithAiModal from 'Pages/CampaignCreation/components/GeneratedCampaign/GeneratedCampaignContent/EditWithAIModal/EditWithAiModal';

import type { GenBannerType } from 'types';

import './GeneratedCreatives.scss';

type GeneratedCampaignCreativesProps = {
  changedBannerFiles: { id: string; file: File }[];
  setChangedBannerFiles: Dispatch<SetStateAction<{ id: string; file: File }[]>>;
  campaignData: any;
  selectedItems: string[];
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  onUpdateCampaign: () => Promise<void>;
};
const GeneratedCampaignCreatives = ({
  changedBannerFiles,
  setChangedBannerFiles,
  campaignData,
  selectedItems,
  setSelectedItems,
  onUpdateCampaign,
}: GeneratedCampaignCreativesProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [openEditBannerWithAIModal, setOpenEditBannerWithAIModal] = useState(false);
  const [editWithAiBanner, setEditWithAiBanner] = useState<GenBannerType | null>(null);

  const [openEditBannerInGenerator, setOpenEditBannerInGenerator] = useState(false);

  const next = () => {
    setActiveIndex(prevState =>
      prevState === campaignData.gen_banners.length - 1 ? 0 : prevState + 1
    );
  };
  const prev = () => {
    setActiveIndex(prevState => (prevState === 0 ? 0 : prevState - 1));
  };

  const unselect = (id: string) => {
    setSelectedItems(prev => prev.filter(bannerId => bannerId !== id));
  };
  const select = (id: string) => {
    setSelectedItems(prev => {
      if (!prev.includes(id)) {
        return [...prev, id];
      }
      return prev;
    });
  };

  const itemTemplate = (item: GenBannerType) => {
    let url = item.presigned_url;
    const changedBanner = changedBannerFiles.find(({ id }) => id === item.banner_uuid);

    if (changedBanner) {
      url = URL.createObjectURL(changedBanner.file);
    }

    return (
      <div className={classNames('image-preview-container')}>
        <ToggleMenu menuItems={menuItems(item)} isButtonDisable={false} outlined />
        <div className="selected-img-container">
          <button className="carusel-btn prev" onClick={prev} disabled={activeIndex === 0}>
            <FontAwesomeIcon
              icon={faChevronLeft}
              color={activeIndex === 0 ? '#B0B0B0' : '#4F4F4F'}
              style={{ fontSize: 16 }}
            />
          </button>
          <img src={url} alt={item.banner_uuid} className="image-preview" />
          <button className="carusel-btn next" onClick={next}>
            <FontAwesomeIcon icon={faChevronRight} color="#4F4F4F" style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>
    );
  };
  const thumbnailTemplate = (item: GenBannerType) => {
    let url = item.presigned_url;
    const changedBanner = changedBannerFiles.find(({ id }) => id === item.banner_uuid);

    if (changedBanner) {
      url = URL.createObjectURL(changedBanner.file);
    }

    return (
      <div className="gallery-image-preview-container" key={item.banner_uuid}>
        {isSelectedCreative(item.banner_uuid) && (
          <CustomIcons name="check_circle" className="check" />
        )}
        <img src={url} alt={item.banner_uuid} className="gallery-preview-image" />
      </div>
    );
  };
  const isSelectedCreative = (id: string) => selectedItems.includes(id);

  const includeExclude = (id: string) => {
    return {
      label: isSelectedCreative(id) ? 'Exclude this Ad' : 'Include this Ad',
      className: isSelectedCreative(id) ? 'exclude' : 'include',
      icon: isSelectedCreative(id) ? (
        <FontAwesomeIcon icon={faMinusCircle} color="#FF2D38" style={{ fontSize: 16 }} />
      ) : (
        <FontAwesomeIcon icon={faCheck} color="#52C41A" style={{ fontSize: 16 }} />
      ),
      command: () => {
        (isSelectedCreative(id) ? unselect : select)(id);
      },
    };
  };

  const menuItems = (item: GenBannerType) => {
    return [
      {
        label: 'Edit with AI',
        className: 'ai-edit',
        icon: <FontAwesomeIcon icon={faMagic} color="#4F4F4F" style={{ fontSize: 16 }} />,
        command: () => {
          setOpenEditBannerWithAIModal(true);
          setEditWithAiBanner(item);
        },
      },
      {
        label: 'Edit in generator',
        command: () => {
          setOpenEditBannerInGenerator(true);
        },
      },
      includeExclude(item.banner_uuid),
    ];
  };
  const addressList = [
    campaignData.listing?.street,
    campaignData.listing?.city,
    campaignData.listing?.state,
    campaignData.listing?.zip,
  ].filter(address => !!address);

  const goToBannerEditor = () => {
    setOpenEditBannerInGenerator(true);
  };

  return (
    <FlexBox flexDirection="column" UNSAFE_className="creative-container" UNSAFE_style={{}}>
      <h3 className="creative-heading">Review and approve ads for your campaign</h3>
      {addressList.length > 0 && (
        <p className="listing-address">
          Listing address:
          {addressList.join(', ')}
        </p>
      )}
      <FlexBox
        flexDirection="column"
        UNSAFE_className="magic-banners-usage generated-creatives"
        gap="0.5rem"
      >
        <Galleria
          showThumbnailNavigators={false}
          value={campaignData.gen_banners}
          numVisible={campaignData.gen_banners.length}
          circular
          activeIndex={activeIndex}
          onItemChange={e => setActiveIndex(e.index)}
          item={itemTemplate}
          thumbnail={thumbnailTemplate}
        />
      </FlexBox>
      {openEditBannerWithAIModal && (
        <EditWithAiModal
          goToBannerEditor={goToBannerEditor}
          editableBanner={editWithAiBanner}
          onClose={() => {
            setOpenEditBannerWithAIModal(false);
            setEditWithAiBanner(null);
          }}
          onUpdateCampaign={onUpdateCampaign}
        />
      )}
      {openEditBannerInGenerator && (
        <CanvasEditor
          onApproveCanvas={file => {
            setChangedBannerFiles(banners => [
              ...banners,
              { id: campaignData.gen_banners[activeIndex].banner_uuid, file },
            ]);
            setOpenEditBannerInGenerator(false);
            setEditWithAiBanner(null);
            setOpenEditBannerWithAIModal(false);
          }}
          canvas_banner={campaignData.gen_banners[activeIndex].img}
          onCancel={() => setOpenEditBannerInGenerator(false)}
        />
      )}
    </FlexBox>
  );
};

export default GeneratedCampaignCreatives;
